import React from 'react';
import { Container, Section, SectionHeader, Kicker, PullQuote, StatBlock, Reveal } from '../components/ui/Primitives';
import { Button } from '../components/ui/Button';
const PRINCIPLES = [{
  title: 'Evidence before opinion',
  detail: 'We state what we know, how we know it and how confident we are. Where the evidence is thin we say so rather than filling the gap with confidence.'
}, {
  title: 'A person stays accountable',
  detail: 'Every system we build has a named reviewer and a route of appeal. Augmented intelligence means the machine assists the judgement, it does not replace the accountability.'
}, {
  title: 'Capability, not dependency',
  detail: 'We hand over the method, not just the output. If your team cannot repeat the work after we leave, we have not finished.'
}, {
  title: 'Scope agreed in writing',
  detail: 'Every engagement starts with a discovery call and a written proposal setting out scope, timing and fees. Nothing begins before that is agreed.'
}];
export function About() {
  return <>
      <section className="surface-dark" data-id="element-541" data-mp="1pj8gav2zr3ft">
        <Container className="py-16 sm:py-24" data-id="element-542" data-mp="1pk9wfdyv6g4e">
          <Kicker data-id="element-543" data-mp="23i88udtmk2h9">About</Kicker>
          <h1 className="mt-4 max-w-3xl font-display text-[34px] leading-tight text-white sm:text-[46px]" data-id="element-544" data-mp="23j9hycr2yewc">
            A consulting firm built for the decision, not the deck
          </h1>
          <p className="mt-6 max-w-2xl text-[17px] text-white/72" data-id="element-545" data-mp="2dfma366z8koz">
            Cavalla Solution advises organisations across Africa and globally on market intelligence, data led
            growth and augmented intelligence. We were founded on a simple frustration: too much analysis arrives
            after the decision has been made, and too little of it changes anything.
          </p>
        </Container>
      </section>

      <Section aria-labelledby="story" data-id="element-546" data-mp="2w7neeo2it35s">
        <div className="grid gap-12 lg:grid-cols-[1.1fr_0.9fr]" data-id="element-547" data-mp="2r82f2cjfussj">
          <div data-id="element-548" data-mp="2r9vpm2x69x5t">
            <SectionHeader id="story" kicker="Why we exist" title="Made for people, powered by AI" data-id="element-549" data-mp="2r67wv5zjxes5" />
            <div className="mt-6 max-w-prose space-y-5 text-[16px] text-body" data-id="element-550" data-mp="1ostjggjs14en">
              <p data-id="element-551" data-mp="1swhqmy4hxxzd">
                The tools available to an organisation in Accra, Nairobi or Lagos are now the same tools available
                to one in Frankfurt. What differs is the surrounding capability: agreed data, trained people, clear
                governance and the confidence to act on an imperfect answer.
              </p>
              <p data-id="element-552" data-mp="2cjphvwiqmxuv">
                That gap is the work. We close it by pairing machine analysis with human judgement, and by handing
                over the method so the value does not leave when we do.
              </p>
              <p data-id="element-553" data-mp="2ljuf5du7wfvb">
                We serve NGOs, government, banks, health providers, agricultural businesses and small and medium
                enterprises. The sectors differ. The pattern rarely does.
              </p>
            </div>
          </div>
          <PullQuote data-id="element-554" data-mp="jnd4ba38d7bn">
            A report that does not change a decision has failed, however well it is written.
          </PullQuote>
        </div>
      </Section>

      <Section tone="mist" aria-labelledby="principles" data-id="element-555" data-mp="ev22u2suddgz">
        <SectionHeader id="principles" kicker="How we work" title="Four principles we hold to" data-id="element-556" data-mp="9x0k5izbvg61" />
        <div className="mt-10 grid gap-6 sm:grid-cols-2" data-id="element-557" data-mp="4yz2n2hjvowo">
          {PRINCIPLES.map((principle, index) => <Reveal key={principle.title} delay={index * 0.05} data-id="element-558" data-mp="12mh926nqoexi" data-mpi={index}>
              <div className="h-full border-l-2 border-gold bg-white p-6" data-id="element-559" data-mp="xn4oxto95lym">
                <h3 className="font-display text-[21px] leading-snug text-ink" data-id="element-560" data-mp="xl1qqmcy4mit">{principle.title}</h3>
                <p className="mt-3 text-[15px] text-body" data-id="element-561" data-mp="sq4kfn71wr9m">{principle.detail}</p>
              </div>
            </Reveal>)}
        </div>
      </Section>

      <Section tone="champagne" aria-labelledby="numbers" data-id="element-562" data-mp="3dfbqy57ti8e1">
        <SectionHeader id="numbers" kicker="Our first ninety days" title="What we are building towards" data-id="element-563" data-mp="39coxy0zeuhf9" />
        <div className="mt-10 grid gap-10 sm:grid-cols-2 lg:grid-cols-4" data-id="element-564" data-mp="1149032ynijod">
          <StatBlock value={10} label="Priority sectors under continuous coverage" data-id="element-565" data-mp="111waq4qkd71a" />
          <StatBlock value={3} label="Service lines, each with three ways to engage" data-id="element-566" data-mp="15w0hamvx3d1u" />
          <StatBlock value={4} label="Steps in the method, run on every engagement" data-id="element-567" data-mp="r7e16iyguh9k" />
          <StatBlock value={30} suffix=" min" label="Discovery call, with the founder" data-id="element-568" data-mp="w5xlyg1bpn0c" />
        </div>
        <div className="mt-10 flex flex-col gap-3 sm:flex-row" data-id="element-569" data-mp="1jvs1x6gxvrot">
          <Button to="/contact" size="lg" data-id="element-570" data-mp="1otbhc9pdiaic">Book a discovery call</Button>
          <Button to="/services" variant="secondary" size="lg" data-id="element-571" data-mp="1or7d1qnkb9hj">See our services</Button>
        </div>
      </Section>
    </>;
}